import { EIP712Signer } from './EIP712Signer.js';
import {
  createSacrificeDomain,
  defaultChainId
} from './constants.js';
import type { Hex } from 'viem';
import type { NetworkType } from './network-types.js';

// Strongly typed message structures
export interface ValidateMessage {
  purpose: typeof loginString;
  projectId: string;
  timestamp: bigint;
  termsOfUse: string;
}

export interface InstanceLoginMessage {
  purpose: typeof instanceLoginString;
  timestamp: bigint;
}

export interface AddressGenerationMessage {
  networkType: string;
  chainId: bigint;
  projectId: string;
  timestamp: bigint;
}

// Strongly typed type definitions using const assertions for exact typing
export const validateTypes = {
  Validate: [
    { name: 'purpose', type: 'string' },
    { name: 'projectId', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
    { name: 'termsOfUse', type: 'string' },
  ],
} as const;

export const instanceLoginTypes = {
  InstanceLogin: [
    { name: 'purpose', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
  ],
} as const;

export const addressGenerationTypes = {
  AddressGeneration: [
    { name: 'projectId', type: 'string' },
    { name: 'networkType', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'timestamp', type: 'uint256' },
  ],
} as const;

// Root address access: sign over timestamp + networkId
export interface RootAccessMessage {
  timestamp: bigint;
  networkType: string;
  chainId: bigint;
}

export const rootAccessTypes = {
  RootAccess: [
    { name: 'timestamp', type: 'uint256' },
    { name: 'networkType', type: 'string' },
    { name: 'chainId', type: 'uint256' },
  ],
} as const;

export const logType = {
  NATIVE: 'native',
  TOKEN: 'token',
  MUTATION: 'mutation',
} as const;

export const logTypeList = Object.values(logType) as [LogType, ...LogType[]];

export type LogType = (typeof logType)[keyof typeof logType];

// Report: sign over sacrificeId + message + timestamp
export interface ReportMessage {
  projectId: string;
  networkType: string;
  chainId: bigint;
  blockHash: string;
  tokenAddress: string;
  logIndex: number;
  logType: LogType;
  transactionHash: string;
  timestamp: bigint;
  message: string;
}

export const reportTypes = {
  Report: [
    { name: 'projectId', type: 'string' },
    { name: 'networkType', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'blockHash', type: 'string' },
    { name: 'tokenAddress', type: 'string' },
    { name: 'transactionHash', type: 'string' },
    { name: 'logIndex', type: 'uint32' },
    { name: 'logType', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
    { name: 'message', type: 'string' },
  ],
} as const;

// Project Creation: sign over the full identity + terms + curve + runtime-config
// field set, symmetric with Project Update so a created campaign is born complete.
//
// Genesis fields (incrementMode, registryAddress) are included HERE and deliberately
// omitted from ProjectUpdateMessage / projectUpdateTypes. This structural omission is
// the machine-enforced genesis-immutability guarantee (spec §7 / D-S3-4): a field
// that is absent from the update type definition cannot appear in any update-path
// signature, so the genesis values are effectively unsignable after creation.
//
// Optional fields follow the repo convention: string fields where empty string = absent.
// incrementMode: 'offchain' is the default for legacy campaigns; registryAddress: ''
// means no registry (off-chain mode). On-chain campaigns supply both.
export interface ProjectCreationMessage {
  projectId: string;
  timestamp: bigint;
  projectName: string;
  projectDescription: string;
  projectWebsite: string;
  projectLogo: string;
  projectTagline: string;
  projectAccentColor: string;
  projectAccentColorVariant: string;
  projectBanner: string;
  projectDisclaimerShort: string;
  projectDisclaimerLong: string;
  projectSocialLinks: string;
  projectTags: string;
  startTime: bigint;
  inflectionTime: bigint;
  endTime: bigint;
  disabled: boolean;
  decayRateSingleStep: bigint;
  decaySingleStepDelaySeconds: bigint;
  volumeBonusRate: bigint;
  usdToPointBaseRate: bigint;
  volumeBonusCutoff: bigint;
  flatBonusCutoff: bigint;
  flatBonusRate: bigint;
  referralRate: bigint;
  countEarlySacrifices: boolean;
  useInflectionStepOne: boolean;
  priceValidFromTimestamp: string;
  /** Genesis: increment mode. Empty string is NOT valid — callers must pass 'offchain'
   *  for legacy campaigns or 'onchain' for registry-backed ones. */
  incrementMode: string;
  /** Genesis: registry contract address. Empty string = absent (off-chain mode). */
  registryAddress: string;
}

export const projectCreationTypes = {
  ProjectCreation: [
    { name: 'projectId', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
    { name: 'projectName', type: 'string' },
    { name: 'projectDescription', type: 'string' },
    { name: 'projectWebsite', type: 'string' },
    { name: 'projectLogo', type: 'string' },
    { name: 'projectTagline', type: 'string' },
    { name: 'projectAccentColor', type: 'string' },
    { name: 'projectAccentColorVariant', type: 'string' },
    { name: 'projectBanner', type: 'string' },
    { name: 'projectDisclaimerShort', type: 'string' },
    { name: 'projectDisclaimerLong', type: 'string' },
    { name: 'projectSocialLinks', type: 'string' },
    { name: 'projectTags', type: 'string' },
    { name: 'startTime', type: 'uint256' },
    { name: 'inflectionTime', type: 'uint256' },
    { name: 'endTime', type: 'uint256' },
    { name: 'disabled', type: 'bool' },
    { name: 'decayRateSingleStep', type: 'uint256' },
    { name: 'decaySingleStepDelaySeconds', type: 'uint256' },
    { name: 'volumeBonusRate', type: 'uint256' },
    { name: 'usdToPointBaseRate', type: 'uint256' },
    { name: 'volumeBonusCutoff', type: 'uint256' },
    { name: 'flatBonusCutoff', type: 'uint256' },
    { name: 'flatBonusRate', type: 'uint256' },
    { name: 'referralRate', type: 'uint256' },
    { name: 'countEarlySacrifices', type: 'bool' },
    { name: 'useInflectionStepOne', type: 'bool' },
    { name: 'priceValidFromTimestamp', type: 'string' },
    // Genesis fields — bound here, absent from projectUpdateTypes (immutability guarantee).
    { name: 'incrementMode', type: 'string' },
    { name: 'registryAddress', type: 'string' },
  ],
} as const;

// Project Update: sign over projectId + timestamp + update fields
export interface ProjectUpdateMessage {
  projectId: string;
  timestamp: bigint;
  projectName: string;
  projectWebsite: string;
  projectDescription: string;
  projectLogo: string;
  projectTagline: string;
  projectAccentColor: string;
  projectAccentColorVariant: string;
  projectBanner: string;
  projectDisclaimerShort: string;
  projectDisclaimerLong: string;
  projectSocialLinks: string;
  projectTags: string;
  startTime: bigint;
  inflectionTime: bigint;
  endTime: bigint;
  decaySingleStepDelaySeconds: bigint;
  volumeBonusCutoff: bigint;
  flatBonusCutoff: bigint;
  decayRateSingleStep: bigint;
  usdToPointBaseRate: bigint;
  flatBonusRate: bigint;
  referralRate: bigint;
  countEarlySacrifices: boolean;
  useInflectionStepOne: boolean;
}

export const projectUpdateTypes = {
  ProjectUpdate: [
    { name: 'projectId', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
    { name: 'projectName', type: 'string' },
    { name: 'projectWebsite', type: 'string' },
    { name: 'projectDescription', type: 'string' },
    { name: 'projectLogo', type: 'string' },
    { name: 'projectTagline', type: 'string' },
    { name: 'projectAccentColor', type: 'string' },
    { name: 'projectAccentColorVariant', type: 'string' },
    { name: 'projectBanner', type: 'string' },
    { name: 'projectDisclaimerShort', type: 'string' },
    { name: 'projectDisclaimerLong', type: 'string' },
    { name: 'projectSocialLinks', type: 'string' },
    { name: 'projectTags', type: 'string' },
    { name: 'startTime', type: 'uint256' },
    { name: 'inflectionTime', type: 'uint256' },
    { name: 'endTime', type: 'uint256' },
    { name: 'decaySingleStepDelaySeconds', type: 'uint256' },
    { name: 'volumeBonusCutoff', type: 'uint256' },
    { name: 'flatBonusCutoff', type: 'uint256' },
    { name: 'decayRateSingleStep', type: 'uint256' },
    { name: 'usdToPointBaseRate', type: 'uint256' },
    { name: 'flatBonusRate', type: 'uint256' },
    { name: 'referralRate', type: 'uint256' },
    { name: 'countEarlySacrifices', type: 'bool' },
    { name: 'useInflectionStepOne', type: 'bool' },
  ],
} as const;

export const projectUpdateSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  projectUpdateTypes,
  'ProjectUpdate'
);

// Network Management: sign over projectId + networkType + chainId + action + timestamp + network properties
export type NetworkManagementAction = 'add' | 'remove' | 'update';

export interface NetworkManagementMessage {
  projectId: string;
  networkType: NetworkType;
  chainId: bigint;
  action: NetworkManagementAction;
  timestamp: bigint;
  rootAddress: string;
  networkName: string;
  lowestBlock: bigint;
  lowerRange: bigint;
  upperRange: bigint;
  rpcUrls: string;
}

export const networkManagementTypes = {
  NetworkManagement: [
    { name: 'projectId', type: 'string' },
    { name: 'networkType', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'action', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
    { name: 'rootAddress', type: 'string' },
    { name: 'networkName', type: 'string' },
    { name: 'lowestBlock', type: 'uint256' },
    { name: 'lowerRange', type: 'uint256' },
    { name: 'upperRange', type: 'uint256' },
    { name: 'rpcUrls', type: 'string' },
  ],
} as const;

export type TokenManagementAction = 'add' | 'remove' | 'update';

export interface TokenManagementMessage {
  projectId: string;
  networkType: NetworkType;
  chainId: bigint;
  action: TokenManagementAction;
  timestamp: bigint;
  tokenAddress: string;
  source: string;
  market: string;
  searchable: string;
}

export const tokenManagementTypes = {
  TokenManagement: [
    { name: 'projectId', type: 'string' },
    { name: 'networkType', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'action', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
    { name: 'tokenAddress', type: 'string' },
    { name: 'source', type: 'string' },
    { name: 'market', type: 'string' },
    { name: 'searchable', type: 'string' },
  ],
} as const;

export const loginString = 'sacrifice_address_login'

// Instance admin sign-in: project-independent, binds only purpose + timestamp
// so an admin can prove wallet control without being tied to any campaign.
export const instanceLoginString = 'altar_instance_admin_login'

// Create the two signer instances
export const validationSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  validateTypes,
  'Validate'
);

export const instanceLoginSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  instanceLoginTypes,
  'InstanceLogin'
);

export const addressGenerationSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  addressGenerationTypes,
  'AddressGeneration'
);

export const rootAccessSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  rootAccessTypes,
  'RootAccess'
);

export const reportSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  reportTypes,
  'Report'
);

export const projectCreationSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  projectCreationTypes,
  'ProjectCreation'
);

export const networkManagementSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  networkManagementTypes,
  'NetworkManagement'
);

export const tokenManagementSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  tokenManagementTypes,
  'TokenManagement'
);

// Admin Management: sign over projectId + address + action + timestamp.
// Grants or revokes an admin role on a project. Any existing project admin may
// sign; the backend rejects removing the project's last admin (lock-out guard).
export type AdminManagementAction = 'add' | 'remove';

export interface AdminManagementMessage {
  projectId: string;
  address: string;
  action: AdminManagementAction;
  timestamp: bigint;
}

export const adminManagementTypes = {
  AdminManagement: [
    { name: 'projectId', type: 'string' },
    { name: 'address', type: 'string' },
    { name: 'action', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
  ],
} as const;

export const adminManagementSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  adminManagementTypes,
  'AdminManagement'
);

// Referral: sign over projectId + referrerAddress + timestamp
export interface ReferralSubmissionMessage {
  projectId: string;
  referrerAddress: string;
  timestamp: bigint;
}

export const referralSubmissionTypes = {
  ReferralSubmission: [
    { name: 'projectId', type: 'string' },
    { name: 'referrerAddress', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
  ],
} as const;

export const referralSubmissionSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  referralSubmissionTypes,
  'ReferralSubmission'
);

// Campaign Import: sign over the canonical serialized campaign-config artifact (a
// single string — viem keccak-hashes it during EIP-712 encoding, so no bespoke hash
// is needed) plus a timestamp. The recovered signer becomes the new campaign's admin.
export interface CampaignImportMessage {
  config: string;
  timestamp: bigint;
}

export const campaignImportTypes = {
  CampaignImport: [
    { name: 'config', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
  ],
} as const;

export const campaignImportSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  campaignImportTypes,
  'CampaignImport'
);

// ---------------------------------------------------------------------------
// Sacrifice Mutation + Report Toggle — dedicated action signers
//
// These two admin writes previously rode on the generic per-campaign login
// signature, which made that signature a bearer credential for arbitrary
// leaderboard-affecting payloads. Each message below binds EVERY field the
// backend handler writes, so a captured signature authorizes exactly one
// payload and nothing else.
//
// Optional fields ride as empty-string sentinels. Optional NUMERIC fields are
// encoded as decimal strings (not zero sentinels) because zero is a legitimate
// value for them (userAddressListIndex 0, tokenDecimals 0): '' (absent) must
// hash differently from '0' (present zero) or the signature would authorize
// two different effects.
// ---------------------------------------------------------------------------

export interface SacrificeMutationMessage {
  projectId: string;
  networkType: string;
  chainId: bigint;
  blockHash: string;
  transactionHash: string;
  tokenAddress: string;
  logIndex: number;
  logType: string;
  action: string;
  timestamp: bigint;
  amount: string;
  pointsDestinationAddress: string;
  userAddressListIndex: string;
  source: string;
  market: string;
  // The sacrifice row's own timestamp ('add' action); named to avoid colliding
  // with the signature timestamp above.
  sacrificeTimestamp: string;
  blockNumber: string;
  blockStatus: string;
  tokenSymbol: string;
  tokenName: string;
  tokenDecimals: string;
  price: string;
  reason: string;
}

export const sacrificeMutationTypes = {
  SacrificeMutation: [
    { name: 'projectId', type: 'string' },
    { name: 'networkType', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'blockHash', type: 'string' },
    { name: 'transactionHash', type: 'string' },
    { name: 'tokenAddress', type: 'string' },
    { name: 'logIndex', type: 'uint32' },
    { name: 'logType', type: 'string' },
    { name: 'action', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
    { name: 'amount', type: 'string' },
    { name: 'pointsDestinationAddress', type: 'string' },
    { name: 'userAddressListIndex', type: 'string' },
    { name: 'source', type: 'string' },
    { name: 'market', type: 'string' },
    { name: 'sacrificeTimestamp', type: 'string' },
    { name: 'blockNumber', type: 'string' },
    { name: 'blockStatus', type: 'string' },
    { name: 'tokenSymbol', type: 'string' },
    { name: 'tokenName', type: 'string' },
    { name: 'tokenDecimals', type: 'string' },
    { name: 'price', type: 'string' },
    { name: 'reason', type: 'string' },
  ],
} as const;

export const sacrificeMutationSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  sacrificeMutationTypes,
  'SacrificeMutation'
);

/**
 * The pre-encoding shape of a sacrifice-mutation signing request: required
 * identity fields plus the optional override fields as their natural types.
 * Both the signing hook and the backend handler convert this to the message
 * via {@link createSacrificeMutationMessage}, so the sentinel encoding cannot
 * drift between the two sides.
 */
export interface SacrificeMutationSigningInputs {
  projectId: string;
  networkType: string;
  chainId: number;
  blockHash: string;
  transactionHash: string;
  tokenAddress: string;
  logIndex: number;
  logType: string;
  action: string;
  timestamp: bigint;
  amount?: string;
  pointsDestinationAddress?: string;
  userAddressListIndex?: number;
  source?: string;
  market?: string;
  sacrificeTimestamp?: string;
  blockNumber?: string;
  blockStatus?: string;
  tokenSymbol?: string;
  tokenName?: string;
  tokenDecimals?: number;
  price?: string;
  reason?: string;
}

const encodeOptionalText = (value: string | undefined): string => value ?? '';

const encodeOptionalNumber = (value: number | undefined): string =>
  value === undefined ? '' : String(value);

/**
 * Build the canonical SacrificeMutation message from signing inputs.
 * Single source of truth for the optional-field sentinel encoding.
 */
export const createSacrificeMutationMessage = (
  inputs: SacrificeMutationSigningInputs
): SacrificeMutationMessage => ({
  projectId: inputs.projectId,
  networkType: inputs.networkType,
  chainId: BigInt(inputs.chainId),
  blockHash: inputs.blockHash,
  transactionHash: inputs.transactionHash,
  tokenAddress: inputs.tokenAddress,
  logIndex: inputs.logIndex,
  logType: inputs.logType,
  action: inputs.action,
  timestamp: inputs.timestamp,
  amount: encodeOptionalText(inputs.amount),
  pointsDestinationAddress: encodeOptionalText(inputs.pointsDestinationAddress),
  userAddressListIndex: encodeOptionalNumber(inputs.userAddressListIndex),
  source: encodeOptionalText(inputs.source),
  market: encodeOptionalText(inputs.market),
  sacrificeTimestamp: encodeOptionalText(inputs.sacrificeTimestamp),
  blockNumber: encodeOptionalText(inputs.blockNumber),
  blockStatus: encodeOptionalText(inputs.blockStatus),
  tokenSymbol: encodeOptionalText(inputs.tokenSymbol),
  tokenName: encodeOptionalText(inputs.tokenName),
  tokenDecimals: encodeOptionalNumber(inputs.tokenDecimals),
  price: encodeOptionalText(inputs.price),
  reason: encodeOptionalText(inputs.reason),
});

export interface ReportToggleMessage {
  projectId: string;
  networkType: string;
  chainId: bigint;
  blockHash: string;
  tokenAddress: string;
  transactionHash: string;
  logIndex: number;
  logType: string;
  signerAddress: string;
  ignored: boolean;
  timestamp: bigint;
}

export const reportToggleTypes = {
  ReportToggle: [
    { name: 'projectId', type: 'string' },
    { name: 'networkType', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'blockHash', type: 'string' },
    { name: 'tokenAddress', type: 'string' },
    { name: 'transactionHash', type: 'string' },
    { name: 'logIndex', type: 'uint32' },
    { name: 'logType', type: 'string' },
    { name: 'signerAddress', type: 'string' },
    { name: 'ignored', type: 'bool' },
    { name: 'timestamp', type: 'uint256' },
  ],
} as const;

export const reportToggleSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  reportToggleTypes,
  'ReportToggle'
);

// ---------------------------------------------------------------------------
// Address Pool Upload — dedicated signer
//
// One admin signature covers an entire uploaded address pool. Every field that
// determines the pool's identity and content is bound per the dedicated-signer
// discipline: projectId, networkType, chainId, rangeStart, count, merkleRoot,
// and timestamp. A captured signature authorizes exactly this pool and nothing else.
// ---------------------------------------------------------------------------

export interface AddressPoolUploadMessage {
  projectId: string
  networkType: string
  chainId: bigint
  rangeStart: bigint
  count: bigint
  /** 32-byte hex root of the Merkle tree over the uploaded addresses. bytes32 in typed data. */
  merkleRoot: Hex
  timestamp: bigint
}

export const addressPoolUploadTypes = {
  AddressPoolUpload: [
    { name: 'projectId', type: 'string' },
    { name: 'networkType', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'rangeStart', type: 'uint256' },
    { name: 'count', type: 'uint256' },
    { name: 'merkleRoot', type: 'bytes32' },
    { name: 'timestamp', type: 'uint256' },
  ],
} as const

export const addressPoolUploadSigner = new EIP712Signer(
  createSacrificeDomain(defaultChainId),
  addressPoolUploadTypes,
  'AddressPoolUpload'
);

/** Pre-encoding shape of a report-toggle signing request (chainId as number). */
export interface ReportToggleSigningInputs {
  projectId: string;
  networkType: string;
  chainId: number;
  blockHash: string;
  tokenAddress: string;
  transactionHash: string;
  logIndex: number;
  logType: string;
  signerAddress: string;
  ignored: boolean;
  timestamp: bigint;
}

/** Build the canonical ReportToggle message from signing inputs. */
export const createReportToggleMessage = (
  inputs: ReportToggleSigningInputs
): ReportToggleMessage => ({
  projectId: inputs.projectId,
  networkType: inputs.networkType,
  chainId: BigInt(inputs.chainId),
  blockHash: inputs.blockHash,
  tokenAddress: inputs.tokenAddress,
  transactionHash: inputs.transactionHash,
  logIndex: inputs.logIndex,
  logType: inputs.logType,
  signerAddress: inputs.signerAddress,
  ignored: inputs.ignored,
  timestamp: inputs.timestamp,
});
